/**
 * Central type definitions for UseCelo SDK
 */

// Re-export feature types
export * from './feature';
import type { FallbackBehavior, FeatureAccess, UserPlan, UserSubscription } from './feature';

// Re-export payment types
export type {
  PaymentGateway,
  IntegrationType,
  SubscriptionStatus,
  CheckoutOptions,
  CheckoutSession,
  GatewayInfo,
  GatewayCapabilities,
  SubscriptionDetails,
  PaymentMethod,
  Invoice,
  PortalSessionOptions,
  SubscriptionCancelOptions,
  SubscriptionPauseOptions,
  Currency,
  CurrentSubscriptionData
} from './payment';
import type {
  CheckoutOptions,
  CheckoutSession,
  GatewayInfo,
  SubscriptionDetails,
  PaymentMethod,
  PortalSessionOptions,
  SubscriptionCancelOptions,
  SubscriptionPauseOptions,
  CurrentSubscriptionData
} from './payment';

// Auth types removed - now using simple userId approach

// Core SDK configuration
export interface UseCeloConfig {
  // Required config
  appId: string;
  publicKey: string;

  // API configuration
  apiHost?: string;

  // Auth configuration - simplified approach
  userId?: string | null | (() => string | null | Promise<string | null>);

  // Feature configuration
  fallbackBehavior?: FallbackBehavior;
  cacheTimeout?: number;
  onFeatureBlock?: (featureName: string) => void;
  onUpgradeClick?: (featureName: string) => void;

  // Payment configuration
  enablePayments?: boolean;
  autoLoadGatewayInfo?: boolean;
  onGatewayInfoLoaded?: (info: GatewayInfo) => void;
  onPaymentError?: (error: string) => void;

  // Cache configuration
  enableCache?: boolean;
  cacheTTL?: number;

  // Legacy API key support (use publicKey instead)
  apiKey?: string;
}

// API Response types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface User {
  id: string;
  email?: string;
  name?: string;
  metadata?: Record<string, unknown>;
}

export interface Plan {
  id: string;
  name: string;
  description?: string;
  features?: string[];
  pricing?: {
    amount: number;
    currency: string;
    interval?: string;
  };
}

export interface Feature {
  id: string;
  tag: string;
  name: string;
  description?: string;
  type?: string;
  config?: unknown;
}

export interface Subscription {
  id: string;
  userId: string;
  planId: string;
  status: string;
  currentPeriodEnd?: Date;
  features?: Feature[];
}

export interface FeatureAccessResult {
  hasAccess: boolean;
  feature?: Feature;
  reason?: string;
}

// Error types
export class ApiError extends Error {
  constructor(message: string, public code?: string, public status?: number) {
    super(message);
    this.name = 'ApiError';
  }
}

export class AuthError extends Error {
  constructor(message: string, public code?: string) {
    super(message);
    this.name = 'AuthError';
  }
}

export class NetworkError extends Error {
  constructor(message: string, public originalError?: Error) {
    super(message);
    this.name = 'NetworkError';
  }
}

export interface UseCeloContextValue {
  features: FeatureAccess[];
  loading: boolean;
  error: string | null;
  userPlan: UserPlan | null;
  subscription: UserSubscription | null;

  // Auth - simplified approach
  userId?: string | null | (() => string | null | Promise<string | null>);

  checkAccess: (featureName: string) => boolean;
  trackUsage: (usage: Array<{ featureTag: string; quantity?: number; metadata?: Record<string, unknown> }>) => Promise<void>;
  trackSingleUsage: (featureTag: string, quantity?: number) => Promise<void>;
  refreshFeatures: () => Promise<void>;
  getFeature: (featureTag: string) => FeatureAccess | undefined;
  hasFeature: (featureTag: string) => boolean;

  appId: string;
  publicKey: string;
  apiHost: string;
}

// Complete context type with payment methods
export interface UseCeloContextType extends UseCeloContextValue {
  // Payment integration (when enabled)
  paymentClient?: any;
  gatewayInfo?: GatewayInfo | null;
  isPaymentConfigured?: boolean;

  // Payment methods (when enabled)
  createCheckout?: (options: CheckoutOptions) => Promise<CheckoutSession>;
  getCheckoutUrl?: (planId: string, options?: Partial<CheckoutOptions>) => Promise<string>;
  createPortalSession?: (options?: PortalSessionOptions) => Promise<{ url: string }>;
  getSubscription?: (userId?: string) => Promise<CurrentSubscriptionData | null>;
  cancelSubscription?: (options?: SubscriptionCancelOptions) => Promise<void>;
  pauseSubscription?: (options?: SubscriptionPauseOptions) => Promise<void>;
  resumeSubscription?: (userId?: string) => Promise<void>;
  getPaymentMethods?: (userId?: string) => Promise<PaymentMethod[]>;
  refreshGatewayInfo?: () => Promise<void>;
}

// Re-export from feature types for backwards compatibility
export type { FallbackBehavior } from './feature';

// Re-export subscription manager types
export type {
  // Core types
  SubscriptionDisplay,
  PlanDetails,
  PriceInfo,
  BillingInterval,
  PlanFeatureDetails,

  // Extended Payment types
  PaymentMethodDisplay,
  PaymentMethodType,
  CardBrand,

  // Extended Invoice types
  InvoiceDisplay,
  InvoiceStatus,
  InvoiceItem,

  // Component props
  SubscriptionManagerProps,
  CustomStyles,

  // Action types
  PlanChangeRequest,
  CancelRequest,
  PauseRequest,
  PaymentMethodUpdate,

  // Retention types
  RetentionOffer,
  CancelReason,
  CancelReasonCode,

  // State types
  SubscriptionState,
  SubscriptionAction,

  // Utility types
  DateRange,
  UsageMetric,
  ProrationDetails,

  // Hook return types
  UseSubscriptionReturn,
  UsePaymentMethodsReturn,
  UseBillingHistoryReturn,

  // Error type
  SubscriptionError
} from './subscription-manager.types';

// Additional types for API client
export interface IdentifyResponse {
  success: boolean;
  user?: User;
}

export interface PlanChangeResponse {
  success: boolean;
  subscription?: Subscription;
}

export interface CancelResponse {
  success: boolean;
  message?: string;
}

export interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiry: number;
}

export interface CacheManager {
  get<T>(key: string): CacheEntry<T> | null;
  set<T>(key: string, value: T, ttl?: number): void;
  delete(key: string): boolean;
  clear(): void;
}